import React, { useState } from 'react';
import { 
  Terminal, 
  Copy, 
  Check, 
  ShieldCheck, 
  Search, 
  ExternalLink, 
  Layers, 
  SlidersHorizontal, 
  Cloud, 
  Database,
  Download,
  CheckCircle2,
  FileCode,
  Zap,
  Filter,
  Flame
} from 'lucide-react';
import { CONTROLSPEC_SIEM_PACKS, SiemQueryPack } from '../data/controlSpecPacks';

interface SiemQueryExporterProps {
  agentId?: string;
}

export const SiemQueryExporter: React.FC<SiemQueryExporterProps> = () => {
  const [activePlatform, setActivePlatform] = useState<'azure' | 'aws' | 'splunk'>('azure');
  const [selectedPackId, setSelectedPackId] = useState<string>(CONTROLSPEC_SIEM_PACKS[0]?.id || 'ac-2-3-stale-privileged');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [familyFilter, setFamilyFilter] = useState<string>('all');
  const [copied, setCopied] = useState<boolean>(false);

  const nistFamilies = ['all', ...Array.from(new Set(CONTROLSPEC_SIEM_PACKS.map(p => p.nist_family)))];

  const filteredPacks = CONTROLSPEC_SIEM_PACKS.filter((pack) => {
    const matchesSearch = 
      pack.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pack.control_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pack.objective.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pack.cisa_cpg.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFamily = familyFilter === 'all' || pack.nist_family === familyFilter;
    return matchesSearch && matchesFamily;
  });

  const currentPack: SiemQueryPack = 
    CONTROLSPEC_SIEM_PACKS.find(p => p.id === selectedPackId) || CONTROLSPEC_SIEM_PACKS[0];

  const getActiveQuery = (pack: SiemQueryPack): string => {
    return pack.platforms[activePlatform] || '// Query pending platform compilation.';
  };

  const copyQuery = async () => {
    try {
      await navigator.clipboard.writeText(getActiveQuery(currentPack));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const downloadQuery = () => {
    const query = getActiveQuery(currentPack);
    const ext = activePlatform === 'azure' ? 'kql' : activePlatform === 'aws' ? 'sql' : 'spl';
    const filename = `${currentPack.id}.${activePlatform}.${ext}`;
    const blob = new Blob([query], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Header Banner */}
      <div className="p-6 rounded-3xl bg-linear-to-br from-slate-900 via-indigo-950/80 to-slate-950 border border-indigo-900/40 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Terminal className="w-48 h-48 text-indigo-400" />
        </div>

        <div className="relative z-10 space-y-3 max-w-3xl">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
              ControlSpec Platform-Native SIEM Engine
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> NIST SP 800-53 Rev. 5 & CISA CPG Grounded
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">
            Enterprise SIEM & SOC Detection Query Exporter
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            Deploy platform-native analytics rules to **Microsoft Sentinel (KQL)**, **AWS Athena / CloudWatch SQL**, and **Splunk (SPL)**. Queries connect to Cloudflare D1/R2 event stores with zero manual schema mapping.
          </p>
        </div>
      </div>

      {/* Main Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left Column: Rules & NIST Controls Index (5 cols) */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Search & Filters */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search 13+ NIST controls, CISA CPGs, or queries..."
                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-brand-500 shadow-2xs"
              />
            </div>

            {/* NIST Family Filter Pills */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {nistFamilies.map((fam) => (
                <button
                  key={fam}
                  type="button"
                  onClick={() => setFamilyFilter(fam)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition cursor-pointer ${
                    familyFilter === fam
                      ? 'bg-brand-600 text-white shadow-2xs'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {fam === 'all' ? 'All Families (13)' : fam}
                </button>
              ))}
            </div>
          </div>

          {/* Rules List */}
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {filteredPacks.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                No matching detection rules found.
              </div>
            ) : (
              filteredPacks.map((pack) => {
                const isSelected = currentPack.id === pack.id;
                return (
                  <button
                    key={pack.id}
                    type="button"
                    onClick={() => setSelectedPackId(pack.id)}
                    className={`w-full text-left p-3.5 rounded-2xl transition-all border cursor-pointer ${
                      isSelected
                        ? 'bg-brand-50/80 dark:bg-brand-950/40 border-brand-300 dark:border-brand-500/40 shadow-xs'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-brand-200 dark:hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300">
                          {pack.control_id}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          {pack.nist_family}
                        </span>
                      </div>

                      <span className={`text-[9px] font-mono font-bold px-1.5 py-0.2 rounded uppercase ${
                        pack.severity === 'CRITICAL' ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300' :
                        pack.severity === 'HIGH' ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300' :
                        'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300'
                      }`}>
                        {pack.severity}
                      </span>
                    </div>

                    <div className="font-bold text-xs text-slate-900 dark:text-white leading-snug line-clamp-1">
                      {pack.control_title}
                    </div>

                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                      {pack.objective}
                    </p>

                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[10px]">
                      <span className="text-slate-400 font-mono">{pack.cisa_cpg}</span>
                      <span className="text-brand-600 dark:text-brand-400 font-bold flex items-center gap-1">
                        View KQL / SQL / SPL →
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Code Viewer & Deployment Guide (7 cols) */}
        <div className="lg:col-span-7 space-y-4">
          
          <div className="glass-panel p-6 rounded-3xl space-y-5">
            
            {/* Rule Header */}
            <div className="space-y-2 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-md bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300">
                    {currentPack.control_id}
                  </span>
                  <span className="text-xs font-bold text-slate-500">
                    {currentPack.nist_family}
                  </span>
                </div>
                <span className="text-[11px] font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded">
                  {currentPack.cisa_cpg}
                </span>
              </div>

              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white leading-tight">
                {currentPack.control_title}
              </h3>

              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                {currentPack.objective}
              </p>
            </div>

            {/* Platform Selector Tabs */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-bold">
                {[
                  { id: 'azure', label: 'Microsoft Sentinel (KQL)', icon: Cloud },
                  { id: 'aws', label: 'AWS Athena SQL', icon: Database },
                  { id: 'splunk', label: 'Splunk (SPL)', icon: Terminal }
                ].map((p) => {
                  const isCur = activePlatform === p.id;
                  const Icon = p.icon;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setActivePlatform(p.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition cursor-pointer ${
                        isCur
                          ? 'bg-white dark:bg-slate-800 text-brand-600 dark:text-brand-400 shadow-2xs'
                          : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{p.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons: Copy & Download */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={copyQuery}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 transition cursor-pointer"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copied ? 'Copied' : 'Copy Query'}</span>
                </button>

                <button
                  type="button"
                  onClick={downloadQuery}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white shadow-xs transition cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download</span>
                </button>
              </div>
            </div>

            {/* Code Display Area */}
            <div className="relative rounded-2xl bg-slate-950 p-4 font-mono text-xs text-slate-100 border border-slate-800 overflow-x-auto shadow-inner">
              <pre className="text-[11.5px] leading-relaxed"><code>{getActiveQuery(currentPack)}</code></pre>
            </div>

            {/* Ingestion & Zero-Trust Integration Guide */}
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 space-y-2 text-xs">
              <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-amber-500" />
                <span>Zero-Ingestion Pipeline Setup</span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-[11px] leading-relaxed">
                AgenticScale automatically persists all edge intercepts to Cloudflare D1 and seals hourly Parquet bundles into Cloudflare R2. Point your Sentinel Data Collection Rule (DCR), AWS Athena Glue Table, or Splunk HEC input directly to R2 bucket <code className="text-brand-600 dark:text-brand-400 font-bold font-mono">agenticscale-evidence-cold</code>.
              </p>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
};
